import React, {useEffect, useState, useMemo} from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet";
import { useAuth } from "../../../context/auth"
import {getDigitalSkillsData} from "../../../services/partner.service";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {createLcKey, predefinedSort} from "../../../utils/utils";
import {DQC24_TITLES, INSIDER_MODULE_TYPE} from "../../../utils/constants";

import {
    Paper as MuiPaper,
    Box,
    Grid,
    Card as MuiCard,
    CardContent as MuiCardContent,
    Typography as MuiTypography,
} from "@material-ui/core";

import {Badge} from "../../SchoolDashboard/Baselining/components/Badge.comp"
import { spacing } from "@material-ui/system";
import {GET_APP_LOCALIZE_TEXT} from "../../../utils/dq_lc_service/lc_service";
import RadarChart from "../../SchoolDashboard/Baselining/components/RadarChart";
import {SidePanel} from "../../../components/SidePanel/SidePanel.comp";
import {useLocation} from "react-router-dom";
import {getUniqueModuleAssessL6CodesService, getModuleIndicatorTranslationsService, getScoresOverviewService} from "../../../services/school.service";
import {useGlobalState} from "../../../context/global_state";

const Card = styled(MuiCard)(spacing);

const Paper = styled(MuiPaper)(spacing);

const Typography = styled(MuiTypography)(spacing);

const CardContent = styled(MuiCardContent)`
  position: relative;
`;

const ColouredTypography = styled(Typography)`
  color: ${(props) => props.color ? props.color : props.theme.sidebar.background};
  font-size: ${(props) => props.size ? props.size : "default"};
`

const Spacer = styled.div(spacing);

const DefaultColouredTypography = styled(Typography)`
  color: ${(props) => props.color ? props.color : props.theme.palette.tertiary.main};
  font-size: ${(props) => props.size ? props.size : "default"};
  font-family: "Lemon/Milk";
`

const EfficacyBox = styled(Box)`
  background-color: #f5f5f5;
  border-radius: 16px;
  padding: 24px;
  height: 100%;
`

const EfficacyCard = styled(Box)`
  background-color: #fff;
  border-radius: 0;
  padding: 24px 16px;
  text-align: center;
  height: 100%;
`

const EfficacyTitle = styled(Typography)`
  color: #5BC0DE;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
`

const EfficacyValue = styled(Typography)`
  color: #000;
  font-size: 48px;
  font-weight: bold;
  line-height: 1.2;
`

const EfficacySubtext = styled(Typography)`
  color: #595959;
  font-size: 14px;
  margin-top: 4px;
`

let dq_24_aliases = {}
let dq_24_reverse_aliases = {}

function ScoresOverview() {

    const SELECT_ALL_KEY = 'SELECT_ALL_INDICATORS';
    const MIN_USERS = 10;
    const {authUser} = useAuth();
    const {org_country} = authUser.org_obj;

    const { settings } = useGlobalState();
    const module_code = settings?.selected_report;
    const isAssessmentModule = settings?.selected_module_type === INSIDER_MODULE_TYPE.ASSESSMENT;

    const location = useLocation();
    const pageLc = location.pathname.split('/')[1];

    const [loading, setLoading] = useState(false);
    const [pageData, setPageData] = useState(undefined);

    const [modules, setModules] = useState([]);
    const [indicators, setIndicators] = useState([]);
    const [selectedIndicators, setSelectedIndicators] = useState([]);
    const [indicatorCheckboxState, setIndicatorCheckboxState] = useState({})
    const [sidePanelKey, setSidePanelKey] = useState(0);
    const [nationalScores, setNationalScores] = useState(null);
    const [improvementStats, setImprovementStats] = useState(null);
    const [rubricBaseStats, setRubricBaseStats] = useState([]);

    const selectAllIndicatorsOption = GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__dq_comp_score")
    const secondarySideHeader = GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__select_competency") || "Select a Competency"

    const allActiveIndicators = useMemo(() => {
        return modules.flatMap(module => 
            (module.indicators || []).filter(ind => !ind.disabled).map(ind => ind.indicator_code)
        );
    }, [modules]);

    useEffect(() => {
        initializeDQCodes();
        uniqueAssessL6Codes().then(r => console.log('Unique L6 Codes Fetched!', r));
        loadPageData().then(d => console.log('Digital Competencies Data Fetched!', d));
    }, []);

    useEffect(() => {
        if (selectedIndicators.length > 0) {
            fetchScoresOverview();
        } else {
            setNationalScores(null);
            setImprovementStats(null);
        }
    }, [selectedIndicators]);

    useEffect(() => {
        if (allActiveIndicators.length > 0) {
            const allSelected = allActiveIndicators.every(code => selectedIndicators.includes(code));
            setIndicatorCheckboxState(prevState => ({
                ...prevState,
                [SELECT_ALL_KEY]: allSelected
            }));
        }
    }, [selectedIndicators, allActiveIndicators]);

    useEffect(() => {
        if (allActiveIndicators.length > 0 && selectedIndicators.length === 0) {
            setSelectedIndicators(allActiveIndicators);
            const newState = { [SELECT_ALL_KEY]: true };
            allActiveIndicators.forEach(code => {
                newState[code] = true;
            });
            setIndicatorCheckboxState(newState);
            setSidePanelKey(prev => prev + 1);
        }
    }, [allActiveIndicators]);


    const uniqueAssessL6Codes = async () => {
        if (!module_code) return;
        try {
            setLoading(true);
            const response = await getUniqueModuleAssessL6CodesService(authUser.org_obj.org_code, module_code);
            if (response.data && response.data.modules) {
                // mark disabled for each indicator based on unique_users_count
                console.log("modulesss",response.data.modules);
                const modulesWithDisabled = (response.data.modules || []).map(module => ({
                    ...module,
                    indicators: (module.indicators || []).map(ind => ({
                        ...ind,
                        disabled: typeof ind.unique_users_count === 'number' ? (ind.unique_users_count < MIN_USERS) : false
                    }))
                }));

                // Extract all module codes and indicator codes
                const allModuleCodes = modulesWithDisabled.map(module => module.module_code);
                const allIndicatorCodes = modulesWithDisabled.flatMap(module => (module.indicators || []).map(ind => ind.indicator_code));

                // Fetch translations with default locale 'en'
                const translationsResponse = await getModuleIndicatorTranslationsService('en', allModuleCodes, allIndicatorCodes);

                if (translationsResponse && translationsResponse.data) {
                    console.log("Translations fetched:", translationsResponse.data);
                    const { modules: moduleTranslationsArray = [], indicators: indicatorTranslationsArray = [] } = translationsResponse.data;

                    // Convert arrays to lookup maps for easier access
                    const moduleTranslationsMap = Object.fromEntries(
                        moduleTranslationsArray.map(m => [m.module_code, m.module_name])
                    );
                    const indicatorTranslationsMap = Object.fromEntries(
                        indicatorTranslationsArray.map(i => [i.indicator_code, i.indicator_name])
                    );

                    // Merge translations into modules and indicators
                    const modulesWithTranslations = modulesWithDisabled.map(module => ({
                        ...module,
                        module_name: moduleTranslationsMap[module.module_code] || module.module_code,
                        indicators: (module.indicators || []).map(ind => ({
                            ...ind,
                            indicator_name: indicatorTranslationsMap[ind.indicator_code] || ind.indicator_code
                        }))
                    }));

                    const sortedModules = modulesWithTranslations.sort((a, b) => {
                        const aHasEnabled = (a.indicators || []).some(ind => !ind.disabled);
                        const bHasEnabled = (b.indicators || []).some(ind => !ind.disabled);
                        if (aHasEnabled && !bHasEnabled) return -1;
                        if (!aHasEnabled && bHasEnabled) return 1;
                        return 0;
                    });

                    setModules(sortedModules);
                    setIndicators(allIndicatorCodes);
                    setIndicatorCheckboxState(Object.fromEntries([
                        [SELECT_ALL_KEY, false],
                        ...allIndicatorCodes.map(code => [code, false])
                    ]));
                } else {
                    const sortedModules = modulesWithDisabled.sort((a, b) => {
                        const aHasEnabled = (a.indicators || []).some(ind => !ind.disabled);
                        const bHasEnabled = (b.indicators || []).some(ind => !ind.disabled);
                        if (aHasEnabled && !bHasEnabled) return -1;
                        if (!aHasEnabled && bHasEnabled) return 1;
                        return 0;
                    });

                    setModules(sortedModules);
                    setIndicators(allIndicatorCodes);
                    setIndicatorCheckboxState(Object.fromEntries([
                        [SELECT_ALL_KEY, false],
                        ...allIndicatorCodes.map(code => [code, false])
                    ]));
                }
            }

        } catch (e) {
            //toast warning
            console.log("Error fetching unique L6 codes or translations:", e);
        } finally {
            setLoading(false);
        }

    }

    const fetchScoresOverview = async () => {
        if (!authUser?.org_obj?.org_code || !module_code || selectedIndicators.length === 0) return;

        try {
            setLoading(true);
            const response = await getScoresOverviewService(authUser.org_obj.org_code, module_code, org_country, selectedIndicators);
            const data = response?.data;
            if (!data) return;
            setNationalScores(Array.isArray(data.national_scores) ? data.national_scores : []);
            setImprovementStats(
                Array.isArray(data.improvement_intention) && data.improvement_intention.length > 0
                    ? data.improvement_intention[0] : null);
            setRubricBaseStats(Array.isArray(data.rubric_avg_base) ? data.rubric_avg_base : []);
        } catch (e) {
            console.log("Error fetching scores overview data:", e);
        } finally {
            setLoading(false);
        }
    };

    const initializeDQCodes = () => {
        const dq24_titles = DQC24_TITLES(pageLc);
        dq_24_aliases = {...Object.fromEntries(Object.keys(dq24_titles).map((key) => [key, dq24_titles[key]]))}
        dq_24_reverse_aliases = {...Object.fromEntries(Object.keys(dq24_titles).map((key) => [dq24_titles[key], key]))}
    }

    // Convert score from 1-4 scale to 0-100 scale
    const convertScoreTo100 = (score) => {
        if (score === undefined || score === null || score === 0) return 0;
        return ((score - 1) / 3) * 100;
    }

    function initializeData(data) {
        setPageData(data);
    }

    const loadPageData = async () => {
        setLoading(true);
        try {
            const result = await getDigitalSkillsData(authUser.org_code);
            if (result.data) {
                console.log('Result Data of Digital Competencies --> ', result.data);
                initializeData(result.data)
            }
        } catch (e) {
            console.log("Some error occurred while fetching digital competencies data")
        }
        setTimeout(() => {setLoading(false)}, 200);
    }

    const get_indicator_alias = (code) => {
        if(/.*\D$/.test(code)) return selectAllIndicatorsOption;
        else if(dq_24_aliases[code]) return dq_24_aliases[code];
        return code;
    }

    const get_indicator_reverse_alias = (alias) => {
        if(alias === selectAllIndicatorsOption)
        {
            const indicator = indicators.find((i) => /.*\D$/.test(i))
            if(indicator) return indicator;
        }
        else if(dq_24_reverse_aliases[alias]) return dq_24_reverse_aliases[alias];
        return alias;
    }

    const flattenedData = modules.flatMap(module => {
        const moduleItem = `MODULE:${module.module_code}`;
        const moduleIndicators = (module.indicators || []).map(ind => ind.indicator_code);
        return [moduleItem, ...moduleIndicators];
    });

    const flattenedDataWithSelectAll = [SELECT_ALL_KEY, ...flattenedData];

    const disabledSecondaryItems = modules.flatMap(module => (module.indicators || []).filter(ind => ind.disabled).map(ind => ind.indicator_code));

    const getIndicatorDisplayName = (indicatorCode) => {
        if (indicatorCode === SELECT_ALL_KEY) {
            return GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__select_all", "Select All");
        }
        for (const module of modules) {
            const indicator = (module.indicators || []).find(ind => ind.indicator_code === indicatorCode);
            if (indicator) {
                return indicator.indicator_name || get_indicator_alias(indicatorCode) || indicatorCode;
            }
        }
        return get_indicator_alias(indicatorCode) || indicatorCode;
    };

    // Calculate national average
    const calculateNationalAverage = () => {
        const scoresArray = nationalScores;

        if (!scoresArray || !Array.isArray(scoresArray) || selectedIndicators.length === 0) {
            const avg = pageData && selectedIndicators.length > 0 ? selectedIndicators.reduce((total, current) => {
                return parseFloat(total) + parseFloat(pageData.data[current]?.national_data?.average || 0);
            }, 0.0) / selectedIndicators.length : 0;
            return convertScoreTo100(avg);
        }
        
        // Filter by country and match selected indicators
        const nationalData = scoresArray.filter(item =>
            item.country === org_country && selectedIndicators.includes(item.assess_code)
        );
        
        if (nationalData.length === 0) return 0;
        
        // Calculate average of rubricScore values
        const total = nationalData.reduce((sum, item) => {
            return sum + parseFloat(item.rubricScore || 0);
        }, 0);
        
        const avg = total / nationalData.length;
        return convertScoreTo100(avg);
    };

    // Calculate global average from service response
    const calculateGlobalAverage = () => {
        const scoresArray = nationalScores;
        
        if (!scoresArray || !Array.isArray(scoresArray) || selectedIndicators.length === 0) {
            const avg = pageData && selectedIndicators.length > 0 ? selectedIndicators.reduce((total, current) => {
                return parseFloat(total) + parseFloat(pageData.data[current]?.global_data?.average || 0);
            }, 0.0) / selectedIndicators.length : 0;
            return convertScoreTo100(avg);
        }
        
        // Filter by "Global" country and match selected indicators
        const globalData = scoresArray.filter(item => 
            item.country === "Global" && selectedIndicators.includes(item.assess_code)
        );
        
        if (globalData.length === 0) return 0;
        
        // Calculate average of rubricScore values
        const total = globalData.reduce((sum, item) => {
            return sum + parseFloat(item.rubricScore || 0);
        }, 0);
        
        const avg = total / globalData.length;
        return convertScoreTo100(avg);
    };
    const calculateSchoolAverage = () => {
        if (Array.isArray(rubricBaseStats) && rubricBaseStats.length > 0 && selectedIndicators.length > 0) {
            const baseData = rubricBaseStats.filter(item => selectedIndicators.includes(item.code));
            if (baseData.length > 0) {
                const total = baseData.reduce((sum, item) => sum + parseFloat(item.avg_rub_base || 0), 0);
                const avg = total / baseData.length;
                return convertScoreTo100(avg);
            }
        }
        const avg = pageData && selectedIndicators.length > 0 ? selectedIndicators.reduce((total, current) => {
            return parseFloat(total) + parseFloat(pageData.data[current]?.program_data?.average || 0);
        }, 0.0) / selectedIndicators.length : 0;
        return convertScoreTo100(avg);
    };

    const badge_data = [
        {
            title: GET_APP_LOCALIZE_TEXT(pageLc, "baselining__school_avg") || "School Average",
            value: calculateSchoolAverage(),
        },
        {
            title: GET_APP_LOCALIZE_TEXT(pageLc, "baselining__nation_avg") || "National Average",
            subtitle: `(${org_country})`,
            value: calculateNationalAverage(),
        },
        {
            title: GET_APP_LOCALIZE_TEXT(pageLc, "baselining__glob_avg") || "Global Average",
            value: calculateGlobalAverage(),
        }
    ]

    function onSelectIndicator(item, isChecked, state, setState) {
        if (item === SELECT_ALL_KEY) {
            if (isChecked) {
                const newSelectedIndicators = [...allActiveIndicators];
                setSelectedIndicators(newSelectedIndicators);
                const newState = { ...state, [SELECT_ALL_KEY]: true };
                allActiveIndicators.forEach(code => {
                    newState[code] = true;
                });
                setState(newState);
                setIndicatorCheckboxState(newState);
            } else {
                setSelectedIndicators([]);
                const newState = { ...state, [SELECT_ALL_KEY]: false };
                allActiveIndicators.forEach(code => {
                    newState[code] = false;
                });
                setState(newState);
                setIndicatorCheckboxState(newState);
            }
            return;
        }

        const indicatorCode = item;
        const indicatorObj = modules.flatMap(m => m.indicators || []).find(ind => ind.indicator_code === indicatorCode);
        if (indicatorObj?.disabled) {
            return;
        }

        if(isChecked) {
            setSelectedIndicators(prev => {
                const newIndicators = [...prev, indicatorCode];
                const allSelected = allActiveIndicators.length > 0 && allActiveIndicators.every(code =>
                    newIndicators.includes(code)
                );
                setIndicatorCheckboxState(prevState => ({
                    ...prevState,
                    [indicatorCode]: true,
                    [SELECT_ALL_KEY]: allSelected
                }));
                setState((prevState) => ({
                    ...prevState,
                    [item]: isChecked,
                    [SELECT_ALL_KEY]: allSelected
                }));
                return newIndicators;
            });
        } else {
            setSelectedIndicators(prev => {
                const newIndicators = prev.filter(code => code !== indicatorCode);
                setIndicatorCheckboxState(prevState => ({
                    ...prevState,
                    [indicatorCode]: false,
                    [SELECT_ALL_KEY]: false
                }));
                setState((prevState) => ({
                    ...prevState,
                    [item]: isChecked,
                    [SELECT_ALL_KEY]: false
                }));
                return newIndicators;
            });
        }
    }

    const radarRef = React.createRef();

    const getSchoolIndicatorScore = (indicatorCode) => {
        const baseItem = rubricBaseStats.find(item => item.code === indicatorCode);
        if (baseItem && typeof baseItem.avg_rub_base === 'number') {
            return convertScoreTo100(baseItem.avg_rub_base);
        }
        const score = pageData?.data?.[indicatorCode]?.program_data?.indicator_score || 0;
        return convertScoreTo100(score);
    };

    const getNationalIndicatorScore = (indicatorCode) => {
        const nationalItem = nationalScores?.find(item => item.country === org_country && item.assess_code === indicatorCode);
        if (nationalItem && typeof nationalItem.rubricScore === 'number') {
            return convertScoreTo100(nationalItem.rubricScore);
        }
        const score = pageData?.data?.[indicatorCode]?.national_data?.indicator_score || 0;
        return convertScoreTo100(score);
    };

    const getGlobalIndicatorScore = (indicatorCode) => {
        const globalItem = nationalScores?.find(item => item.country === "Global" && item.assess_code === indicatorCode);
        if (globalItem && typeof globalItem.rubricScore === 'number') {
            return convertScoreTo100(globalItem.rubricScore);
        }
        const score = pageData?.data?.[indicatorCode]?.global_data?.indicator_score || 0;
        return convertScoreTo100(score);
    };

    // Spider/radar chart values: show whole numbers for readability (no decimals)
    const toWholeNumber = (value) => Math.round(value ?? 0);
    const selectedIndicatorScoresSchool = selectedIndicators.map((indicatorCode) => toWholeNumber(getSchoolIndicatorScore(indicatorCode)));
    const selectedIndicatorScoresNational = selectedIndicators.map((indicatorCode) => toWholeNumber(getNationalIndicatorScore(indicatorCode)));
    const selectedIndicatorScoresGlobal = selectedIndicators.map((indicatorCode) => toWholeNumber(getGlobalIndicatorScore(indicatorCode)));

    // When there are lots of indicators, the radar needs more space; stack efficacy below for readability.
    const isDenseRadar = selectedIndicators.length > 5;

    return (
        <React.Fragment>
            <Helmet title={GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__title") || "Scores Overview"} />
            <Box mx={"3vw"}>
                <Box mb={5}>
                    <DefaultColouredTypography variant="h2" fontWeight="fontWeightBold">
                        {GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__title") || "Scores Overview"}
                    </DefaultColouredTypography>
                    <ColouredTypography variant={"body1"} size={"16px"}>
                        {GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__description") || "Understanding the digital competency scores of your program."}
                    </ColouredTypography>
                </Box>
                <Paper container>
                    <Grid container>
                        {/* Left Side Panel */}
                        <Grid item xs={12} sm={12} md={3}>
                            <SidePanel
                                key={sidePanelKey}
                                secondaryHeader={secondarySideHeader}
                                primaryData={[]}
                                secondaryData={flattenedDataWithSelectAll}
                                onCheckSecondary={onSelectIndicator}
                                secondarySingleSelectIndex={undefined}
                                modules={modules}
                                secondaryLabelFormatter={getIndicatorDisplayName}
                                disabledSecondaryItems={disabledSecondaryItems}
                                secondaryCheckboxInitialState={indicatorCheckboxState}
                            />
                        </Grid>
                        {/* Right Side Content */}
                        <Grid item xs={12} sm={12} md={9}>
                            <Card>
                                <CardContent>
                                    <Box sx={{flexGrow: 1}}>
                                        {/* Scores Heading */}
                                        <ColouredTypography mt={5} mb={5} variant={"h4"} align={"center"}>
                                            {GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__scores") || "Scores"}
                                        </ColouredTypography>
                                        {/* 3 Badge Cards in a Row */}
                                        <Grid container spacing={0} align={"center"}>
                                            {badge_data.map((badge, index) => (
                                                    <Grid key={index} item xs={12} sm={4} md={4} lg={4}>
                                                        <Badge
                                                            title={badge.title}
                                                            subtitle={badge.subtitle}
                                                            value={badge.value.toFixed(2)}
                                                        />
                                                    </Grid>
                                                )
                                            )}
                                        </Grid>
                                        <Spacer mb={10}/>
                                        {/* Score Breakdown with Charts */}
                                        <Grid container spacing={6} justifyContent={"space-between"}>
                                            <Grid item xs={12} lg={isDenseRadar ? 12 : 5}>
                                                <ColouredTypography align={"center"} variant={"h4"}>
                                                    {GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__score_breakdown") || "Score Breakdown"}
                                                </ColouredTypography>
                                                <Spacer mb={10}/>
                                                {selectedIndicators.length > 0 && (
                                                    <RadarChart
                                                        ref={radarRef}
                                                        suggestedMin={50}
                                                        min={0}
                                                        max={100}
                                                        stepSize={10}
                                                        tickLabelValues={[0, 50, 100]}
                                                        height={isDenseRadar ? 520 : 420}
                                                        circularGrid={true}
                                                        data={{
                                                            labels: [...selectedIndicators.map((indicator) => getIndicatorDisplayName(indicator))],
                                                            selected: [...selectedIndicators.map(() => true)],
                                                            graphData: [
                                                                {
                                                                    index: 0,
                                                                    country: GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__school") || "School",
                                                                    data: selectedIndicatorScoresSchool,
                                                                    border_opacity: 1.0 ,
                                                                    background_opacity: 0.2,
                                                                    show: true,
                                                                },
                                                                {
                                                                    index: 1,
                                                                    country: GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__national") || "National",
                                                                    data: selectedIndicatorScoresNational,
                                                                    border_opacity: 1.0 ,
                                                                    background_opacity: 0.1,
                                                                    show: true,
                                                                },
                                                                {
                                                                    index: 2,
                                                                    country: GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__global") || "Global",
                                                                    data: selectedIndicatorScoresGlobal,
                                                                    border_opacity: 1.0 ,
                                                                    background_opacity: 0.1,
                                                                    show: true,
                                                                }
                                                            ]
                                                        }}
                                                    />
                                                )}
                                            </Grid>
                                            {/* Efficacy Box */}
                                            {!isAssessmentModule && (
                                            <Grid
                                                item
                                                xs={12}
                                                lg={isDenseRadar ? 12 : 7}
                                                container={isDenseRadar}
                                                justifyContent={isDenseRadar ? "center" : undefined}
                                            >
                                                <EfficacyBox
                                                    style={
                                                        isDenseRadar
                                                            ? { marginTop: 24, width: "80%" }
                                                            : undefined
                                                    }
                                                >
                                                    <Typography variant={"h4"} align={"center"} style={{color: '#000', fontWeight: 'bold', marginBottom: '24px'}}>
                                                        {GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__efficacy") || "Efficacy"}
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6}>
                                                            <EfficacyCard>
                                                                <EfficacyTitle>{GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__improved_learners") || "% Improved Learners"}</EfficacyTitle>
                                                                <EfficacyValue>
                                                                    {improvementStats && improvementStats.unique_users_count
                                                                        ? `${((improvementStats.unique_users_count_improved || 0) / improvementStats.unique_users_count * 100).toFixed(0)}%`
                                                                        : "0%"}
                                                                </EfficacyValue>
                                                                <EfficacySubtext>
                                                                    {improvementStats && improvementStats.unique_users_count
                                                                        ? `(${improvementStats.unique_users_count_improved || 0}/${improvementStats.unique_users_count})`
                                                                        : "(0/0)"}
                                                                </EfficacySubtext>
                                                            </EfficacyCard>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <EfficacyCard>
                                                                <EfficacyTitle>{GET_APP_LOCALIZE_TEXT(pageLc, "scores_overview__intention_to_change") || "% Intention to Change"}</EfficacyTitle>
                                                                <EfficacyValue>
                                                                    {improvementStats && improvementStats.unique_users_count
                                                                        ? `${((improvementStats.unique_users_count_changed || 0) / improvementStats.unique_users_count * 100).toFixed(0)}%`
                                                                        : "0%"}
                                                                </EfficacyValue>
                                                                <EfficacySubtext>
                                                                    {improvementStats && improvementStats.unique_users_count
                                                                        ? `(${improvementStats.unique_users_count_changed || 0}/${improvementStats.unique_users_count})`
                                                                        : "(0/0)"}
                                                                </EfficacySubtext>
                                                            </EfficacyCard>
                                                        </Grid>
                                                    </Grid>
                                                </EfficacyBox>
                                            </Grid>
                                            )}
                                        </Grid>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                    <LoaderWithBackDrop loading={loading}/>
                </Paper>
            </Box>
        </React.Fragment>
    );
}

export default ScoresOverview;
